/**
 * Client wrappers around the admin_* RPCs in
 * supabase/migrations/20260514140000_admin_helpers.sql.
 *
 * Server-side every RPC checks public.is_admin() — these client functions
 * just provide ergonomic typing. If the caller isn't an admin the RPC
 * throws and we re-throw with a clear message.
 */

import { supabase } from "./supabase";

export interface AdminUserSummary {
  user_id: string;
  email: string;
  signup_at: string;
  plan: string | null;
  subscription_status: string | null;
  total_extractions: number;
  completed_extractions: number;
  failed_extractions: number;
  schemas_tried: string[];
  last_extraction_at: string | null;
  credit_balance: number;
  documents_used_month: number;
  documents_used_day: number;
  first_extraction_at: string | null;
  first_extraction_welcome_sent_at: string | null;
}

export async function loadAdminUserSummary(): Promise<AdminUserSummary[]> {
  const { data, error } = await supabase.rpc("admin_user_summary");
  if (error) {
    if (error.message?.toLowerCase().includes("forbidden")) {
      throw new Error("Not authorized — this page requires an admin account.");
    }
    throw new Error(error.message);
  }
  return (data ?? []) as AdminUserSummary[];
}

export async function grantCredits(
  userId: string,
  amount: number,
  note?: string,
): Promise<number> {
  const { data, error } = await supabase.rpc("admin_grant_credits", {
    target_user_id: userId,
    amount,
    reason_note: note,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as number;
}

/**
 * Cheap server-side admin check. Calls the is_admin() RPC directly so it
 * doesn't pull the full user summary just to gate route access.
 */
export async function checkIsAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_admin");
  if (error) return false;
  return Boolean(data);
}
